/**
 * Menu animation shared types — interfaces and constants used by
 * void rise, tank consume, and mortar animation classes.
 */

import type { Graphics, Text } from 'pixi.js';
import type { Camera } from 'bonkjs';
import type { VoidRenderer } from '../void';
import type { ParticleSystem } from '../particles';
import type { ScreenEffects } from '../effects';
import type { ScreenShake } from '../screen-shake';

// ─── Shared dependency bag passed to animation update() ──
export interface MenuAnimDeps {
    voidRenderer: VoidRenderer;
    gameParticles: ParticleSystem;
    effects: ScreenEffects;
    camera: Camera;
    screenShake: ScreenShake;
}

// ─── Tank roster for void animation (from design doc) ──
export interface VoidTankDef {
    name: string;
    shape: 'circle' | 'square' | 'triangle' | 'hexagon' | 'diamond' | 'star' | 'rectangle';
    color: number;
    damagePercent: number;      // 0-200% — affects wall bounce amplification
}

export const TANK_ROSTER: VoidTankDef[] = [
    { name: 'BLAZE', shape: 'square', color: 0xff8800, damagePercent: 120 },
    { name: 'VOLT', shape: 'triangle', color: 0x00ffff, damagePercent: 45 },
    { name: 'PHANTOM', shape: 'hexagon', color: 0xff00ff, damagePercent: 180 },
    { name: 'HIVE', shape: 'hexagon', color: 0xffd700, damagePercent: 30 },
    { name: 'RAZOR', shape: 'diamond', color: 0xff3333, damagePercent: 200 },
    { name: 'NOVA', shape: 'star', color: 0xffffff, damagePercent: 90 },
    { name: 'ORB', shape: 'circle', color: 0x33ff99, damagePercent: 10 },
    { name: 'TITAN', shape: 'rectangle', color: 0x9944ff, damagePercent: 150 },
];

// ─── Menu void rise animation state ──
export interface MenuRiseAnim {
    phase: 'idle' | 'rising' | 'settling' | 'resetting';
    baseVoidY: number;
    riseStartY: number;
    targetVoidY: number;
    idleTimer: number;
    riseElapsed: number;
    resetTimer: number;
    totalRisen: number;
}

// ─── Per-tank state for menu consume animation ──
export interface MenuFlyingTank {
    rosterIndex: number;
    gfx: Graphics;
    x: number;
    y: number;
    vy: number;
    vx: number;
    rotation: number;
    spinRate: number;
    damage: number;
}

// ─── Floating CONSUMED text with independent lifecycle ──
export interface MenuConsumedText {
    text: Text;
    startY: number;
    life: number;
    maxLife: number;
}

// ─── Menu tank consume animation state ──
export interface MenuConsumeAnim {
    phase: 'idle' | 'active';
    idleTimer: number;
    tanks: MenuFlyingTank[];
    consumedTexts: MenuConsumedText[];
}

// ─── Menu mortar state ──
export interface MenuMortar {
    gfx: Graphics;
    x: number;
    y: number;
    vx: number;
    vy: number;
    trail: { x: number; y: number }[];
    isVoidShot: boolean;
    willExplode: boolean;
    pastPeak: boolean;
}

export interface MenuMortarAnim {
    mortars: MenuMortar[];
    idleTimer: number;
    activeVoidShots: number;
}

// Maximum velocity to prevent infinite wall-to-wall acceleration
export const MENU_MAX_SPEED = 1200;

export const MENU_MORTAR = {
    INTERVAL_MIN: 3,
    INTERVAL_MAX: 8,
    GRAVITY: 600,
    TRAIL_MAX: 15,
    RADIUS: 8,
    COLOR: 0x00ffff,
    VOID_COLOR: 0xff00ff,
    VOID_CHANCE: 0.4,
    MAX_VOID_SHOTS: 3,
    VOID_DELAY_MIN: 600,
    VOID_DELAY_MAX: 1200,
} as const;

/** Weighted RNG for tank count: 1=60%, 2=25%, 3=12%, 4=3% */
export function rollTankCount(): number {
    const roll = Math.random();
    if (roll < 0.60) return 1;
    if (roll < 0.85) return 2;
    if (roll < 0.97) return 3;
    return 4;
}
